import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserDoctor,
  faPaperPlane,
  faMicrophone,
  faStop,
  faVolumeHigh,
  faPlus,
  faTrash,
  faFileArrowUp,
  faTriangleExclamation,
  faCheck,
  faShieldHeart,
  faCircleCheck,
  faCircleXmark,
  faSpinner,
  faClipboardList,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import healthAssistantService, {
  type ConsentStatus,
  type Conversation,
  type DocumentVitalRecord,
  type DocumentAnalysisResponse,
  type ConsultationAdvice,
  type Proposal,
  isPrimaryCareOnly,
} from '../../services/healthAssistantService';
import ConfirmDialog, { type ConfirmVariant } from '../../components/ConfirmDialog/ConfirmDialog';
import { useProfile } from '../../context/ProfileContext';
import './AIFamilyDoctor.css';

interface DisplayMessage {
  key: string;
  id?: number | null;
  role: 'user' | 'assistant';
  content: string;
  emergency?: boolean;
  proposals?: (Proposal & { status?: 'saving' | 'saved' | 'error'; note?: string })[];
  consultation?: ConsultationAdvice | null;
}

const VOICE_PREF_KEY = 'familyDoctor.voiceReplies';
const LAST_CONV_KEY = 'familyDoctor.lastConversation';
const MAX_RECORDING_MS = 60_000;

const readVoicePref = () => {
  try {
    return localStorage.getItem(VOICE_PREF_KEY) === '1';
  } catch {
    return false;
  }
};

/** The consultation last opened, per profile, so returning to the page resumes it. */
function readLastConversation(profileKey: string): number | undefined {
  try {
    const raw = localStorage.getItem(LAST_CONV_KEY);
    if (!raw) return undefined;
    const value = (JSON.parse(raw) as Record<string, unknown>)[profileKey];
    return typeof value === 'number' ? value : undefined;
  } catch {
    return undefined;
  }
}

function writeLastConversation(profileKey: string, id: number | undefined) {
  try {
    const raw = localStorage.getItem(LAST_CONV_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    if (id === undefined) delete map[profileKey];
    else map[profileKey] = id;
    localStorage.setItem(LAST_CONV_KEY, JSON.stringify(map));
  } catch {
    /* preference only */
  }
}

function apiError(err: any): { code?: string; message?: string; status?: number } {
  const data = err?.response?.data || {};
  return { code: data.code, message: data.error || data.detail, status: err?.response?.status };
}

/** Renders **bold** and line breaks from the doctor's replies. */
function formatContent(text: string): React.ReactNode[] {
  return text.split('\n').flatMap((line, li) => {
    const parts = line.split(/(\*\*.*?\*\*)/g).map((part, pi) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={`${li}-${pi}`}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={`${li}-${pi}`}>{part}</React.Fragment>,
    );
    return li > 0 ? [<br key={`br-${li}`} />, ...parts] : parts;
  });
}

function describeRecord(r: DocumentVitalRecord) {
  const label = r.vital.replace(/_/g, ' ');
  return r.vital === 'blood_pressure'
    ? `${label}: ${r.systolic}/${r.diastolic} mmHg`
    : `${label}: ${r.value} ${r.unit}`;
}

const AIFamilyDoctor: React.FC = () => {
  const { t, i18n } = useTranslation('familyDoctor');
  const navigate = useNavigate();
  const { conversationId: conversationIdParam } = useParams<{ conversationId?: string }>();
  const { activeProfile } = useProfile();
  const language = (i18n.resolvedLanguage || 'en').split('-')[0];
  const profileKey = activeProfile ? `${activeProfile.type}-${activeProfile.id}` : 'self';

  const [consent, setConsent] = useState<ConsentStatus | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<number | undefined>();
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [voiceReplies, setVoiceReplies] = useState(readVoicePref);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [loadingAudioId, setLoadingAudioId] = useState<number | null>(null);
  const [docBusy, setDocBusy] = useState(false);
  const [analysis, setAnalysis] = useState<DocumentAnalysisResponse | null>(null);
  const [docSelected, setDocSelected] = useState<boolean[]>([]);
  const [savingDocs, setSavingDocs] = useState(false);
  const [docsSaved, setDocsSaved] = useState(false);
  const [confirm, setConfirm] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    variant?: ConfirmVariant;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordTimerRef = useRef<number | null>(null);
  const keySeq = useRef(0);
  const nextKey = () => `m${++keySeq.current}`;
  // Read once on mount; in-app navigation sets state directly, so changing this
  // must not re-run the restore effect.
  const conversationIdParamRef = useRef(conversationIdParam);
  conversationIdParamRef.current = conversationIdParam;

  /** Keep the address bar pointing at the open consultation (refresh/back-safe). */
  const syncUrl = useCallback((id?: number) => {
    const target = id ? `/ai-family-doctor/${id}` : '/ai-family-doctor';
    if (window.location.pathname !== target) navigate(target, { replace: true });
  }, [navigate]);

  // ── Audio ───────────────────────────────────────────────────────────
  const stopAudio = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    audioUrlRef.current = null;
    setPlayingId(null);
  }, []);

  const play = useCallback(async (messageId: number) => {
    stopAudio();
    setLoadingAudioId(messageId);
    try {
      const blob = await healthAssistantService.speak(messageId);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audioUrlRef.current = url;
      audio.onended = stopAudio;
      setPlayingId(messageId);
      await audio.play();
    } catch {
      stopAudio();
      setNotice(t('voiceUnavailable'));
    } finally {
      setLoadingAudioId(null);
    }
  }, [stopAudio, t]);

  const stopRecorder = useCallback((discard = false) => {
    if (recordTimerRef.current) window.clearTimeout(recordTimerRef.current);
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      if (discard) rec.ondataavailable = null;
      if (discard) rec.onstop = () => rec.stream.getTracks().forEach((tr) => tr.stop());
      rec.stop();
    }
    recorderRef.current = null;
    setRecording(false);
  }, []);

  // Stop sound and microphone when leaving the page.
  useEffect(() => () => { stopAudio(); stopRecorder(true); }, [stopAudio, stopRecorder]);

  // ── Loading ─────────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      setConversations(await healthAssistantService.getConversations());
    } catch (err) {
      const e = apiError(err);
      if (e.status === 403) setBlocked(e.message || t('serviceDisabled'));
    }
  }, [t]);

  const startNew = useCallback(async () => {
    stopAudio();
    setActiveId(undefined);
    setInput('');
    setNotice(null);
    setMessages([]);
    writeLastConversation(profileKey, undefined);
    syncUrl(undefined);
    try {
      const insight = await healthAssistantService.getProactiveInsight(language);
      setMessages([{ key: nextKey(), role: 'assistant', content: insight.content }]);
    } catch {
      setMessages([{ key: nextKey(), role: 'assistant', content: t('subtitle') }]);
    }
  }, [language, stopAudio, t, profileKey, syncUrl]);

  useEffect(() => {
    let cancelled = false;
    healthAssistantService.getConsent()
      .then((c) => { if (!cancelled) setConsent(c); })
      .catch((err) => {
        if (cancelled) return;
        const e = apiError(err);
        setBlocked(e.status === 403 ? (e.message || t('serviceDisabled')) : t('error'));
      });
    return () => { cancelled = true; };
  }, [t]);

  // Load an existing consultation. Returns false when it is gone, so callers
  // can fall back to a fresh one.
  const openConversation = useCallback(async (id: number, opts: { silent?: boolean } = {}): Promise<boolean> => {
    stopAudio();
    if (!opts.silent) setNotice(null);
    try {
      const detail = await healthAssistantService.getConversationMessages(id);
      setActiveId(id);
      setMessages(detail.messages.map((m) => ({ key: nextKey(), id: m.id, role: m.role, content: m.content })));
      writeLastConversation(profileKey, id);
      syncUrl(id);
      return true;
    } catch (err) {
      const e = apiError(err);
      if (e.status === 404) writeLastConversation(profileKey, undefined);
      if (!opts.silent) setNotice(t('error'));
      loadConversations();
      return false;
    }
  }, [loadConversations, profileKey, stopAudio, syncUrl, t]);

  // A different family member means different records and consultations, and
  // leaving/returning must resume the consultation the address bar points at
  // (or the one last opened for this profile).
  useEffect(() => {
    if (!consent?.granted || !consent.service_enabled) return;
    let cancelled = false;
    setAnalysis(null);
    setDocSelected([]);
    setDocsSaved(false);
    loadConversations();
    (async () => {
      const fromUrl = conversationIdParamRef.current ? Number(conversationIdParamRef.current) : undefined;
      const target = fromUrl ?? readLastConversation(profileKey);
      if (target && Number.isFinite(target) && await openConversation(target, { silent: true })) return;
      if (!cancelled) startNew();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent?.granted, consent?.service_enabled, profileKey]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  const performDelete = async (id: number) => {
    try {
      await healthAssistantService.deleteConversation(id);
      if (id === activeId) startNew();
      loadConversations();
    } catch {
      setNotice(t('error'));
    }
  };

  const deleteConversation = (id: number) => {
    setConfirm({
      open: true,
      title: t('deleteConsultation'),
      message: t('deleteConfirm'),
      confirmLabel: t('common:buttons.delete'),
      variant: 'danger',
      onConfirm: () => { void performDelete(id); },
    });
  };

  // ── Consent ─────────────────────────────────────────────────────────
  const grant = async () => {
    try {
      await healthAssistantService.grantConsent();
      setConsent((c) => (c ? { ...c, granted: true } : c));
    } catch {
      setNotice(t('error'));
    }
  };

  const performWithdraw = async () => {
    try {
      await healthAssistantService.withdrawConsent();
      stopAudio();
      setConversations([]);
      setMessages([]);
      setActiveId(undefined);
      writeLastConversation(profileKey, undefined);
      syncUrl(undefined);
      setConsent((c) => (c ? { ...c, granted: false } : c));
    } catch {
      setNotice(t('error'));
    }
  };

  const withdraw = () => {
    setConfirm({
      open: true,
      title: t('withdraw'),
      message: t('withdrawConfirm'),
      variant: 'warning',
      onConfirm: () => { void performWithdraw(); },
    });
  };

  // ── Chat ────────────────────────────────────────────────────────────
  const send = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    stopAudio();
    setInput('');
    setNotice(null);
    const pendingKey = nextKey();
    setMessages((prev) => [...prev, { key: pendingKey, role: 'user', content: message }]);
    setSending(true);
    try {
      const res = await healthAssistantService.sendMessage(message, language, activeId);
      const reply: DisplayMessage = {
        key: nextKey(),
        id: res.assistant_message.id,
        role: 'assistant',
        content: res.ai_available ? res.assistant_message.content : t('aiUnavailable'),
        emergency: res.emergency,
        proposals: res.proposals,
        consultation: res.consultation,
      };
      setMessages((prev) => [...prev, reply]);
      if (!activeId) {
        setActiveId(res.conversation_id);
        writeLastConversation(profileKey, res.conversation_id);
        syncUrl(res.conversation_id);
        loadConversations();
      }
      if (voiceReplies && res.assistant_message.id) play(res.assistant_message.id);
    } catch (err) {
      const e = apiError(err);
      setMessages((prev) => prev.filter((m) => m.key !== pendingKey));
      setInput(message);
      if (e.code === 'consent_required') setConsent((c) => (c ? { ...c, granted: false } : c));
      else if (e.status === 403) setBlocked(e.message || t('serviceDisabled'));
      else setNotice(e.message || t('error'));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const updateProposal = (msgKey: string, token: string, patch: Partial<NonNullable<DisplayMessage['proposals']>[number]> | null) => {
    setMessages((prev) => prev.map((m) => (m.key !== msgKey ? m : {
      ...m,
      proposals: (m.proposals || []).flatMap((p) => (p.token !== token ? [p] : patch ? [{ ...p, ...patch }] : [])),
    })));
  };

  const saveProposal = async (msgKey: string, p: Proposal) => {
    updateProposal(msgKey, p.token, { status: 'saving' });
    try {
      await healthAssistantService.confirmProposal(p.token);
      updateProposal(msgKey, p.token, { status: 'saved', note: t('saved') });
    } catch (err) {
      updateProposal(msgKey, p.token, { status: 'error', note: apiError(err).message || t('error') });
    }
  };

  // ── Voice input ─────────────────────────────────────────────────────
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setNotice(t('voiceUnavailable'));
      return;
    }
    stopAudio();
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setNotice(t('micBlocked'));
      return;
    }
    const rec = new MediaRecorder(stream);
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = async () => {
      stream.getTracks().forEach((tr) => tr.stop());
      if (!chunks.length) return;
      setTranscribing(true);
      try {
        const text = await healthAssistantService.transcribe(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }), language);
        // The user reviews what was heard before sending it.
        if (text) setInput((prev) => (prev ? `${prev} ${text}` : text));
        inputRef.current?.focus();
      } catch (err) {
        setNotice(apiError(err).message || t('voiceUnavailable'));
      } finally {
        setTranscribing(false);
      }
    };
    recorderRef.current = rec;
    rec.start();
    setRecording(true);
    setNotice(null);
    recordTimerRef.current = window.setTimeout(() => stopRecorder(), MAX_RECORDING_MS);
  };

  const toggleVoiceReplies = () => {
    const next = !voiceReplies;
    setVoiceReplies(next);
    if (!next) stopAudio();
    try { localStorage.setItem(VOICE_PREF_KEY, next ? '1' : '0'); } catch { /* preference only */ }
  };

  // ── Reports ─────────────────────────────────────────────────────────
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setDocBusy(true);
    setNotice(null);
    setAnalysis(null);
    setDocSelected([]);
    setDocsSaved(false);
    try {
      const res = await healthAssistantService.analyzeDocument(file, language, activeId);
      setAnalysis(res);
      setDocSelected(res.records.map(() => true));
      if (!res.emergency && !res.records.length && !res.recommended_tests.length && !res.consultation.recommended) {
        setNotice(res.message);
      }
      // The reading is persisted as a chat exchange, so show it in the thread
      // and make it the conversation we resume when the page is revisited.
      if (res.conversation_id) {
        if (res.user_message && res.assistant_message) {
          setMessages((prev) => [
            ...prev,
            { key: nextKey(), id: res.user_message!.id, role: 'user', content: res.user_message!.content },
            { key: nextKey(), id: res.assistant_message!.id, role: 'assistant', content: res.assistant_message!.content },
          ]);
        }
        if (res.conversation_id !== activeId) {
          setActiveId(res.conversation_id);
          writeLastConversation(profileKey, res.conversation_id);
          syncUrl(res.conversation_id);
          loadConversations();
        }
      }
    } catch (err) {
      setNotice(apiError(err).message || t('error'));
    } finally {
      setDocBusy(false);
    }
  };

  const saveDocRecords = async () => {
    if (!analysis) return;
    const chosen = analysis.records.filter((_, i) => docSelected[i]);
    if (!chosen.length) return;
    setSavingDocs(true);
    try {
      await healthAssistantService.confirmDocumentExtraction(analysis.review_token, chosen);
      setNotice(t('saved'));
      setDocsSaved(true);
    } catch (err) {
      setNotice(apiError(err).message || t('error'));
    } finally {
      setSavingDocs(false);
    }
  };

  const dismissAnalysis = () => {
    setAnalysis(null);
    setDocSelected([]);
    setDocsSaved(false);
  };

  /** Book a teleconsultation, carrying the report and the AI's first specialization. */
  const bookFromAnalysis = () => {
    if (!analysis) return;
    const params = new URLSearchParams({ report_id: String(analysis.report_id) });
    const spec = analysis.consultation.specializations[0];
    if (spec) params.set('specialization', String(spec.id));
    navigate(`/teleconsultation?${params.toString()}`);
  };

  /** Book from a chat recommendation (no report attached). */
  const bookFromChat = (c: ConsultationAdvice) => {
    const spec = c.specializations[0];
    const qs = spec ? `?specialization=${spec.id}` : '';
    navigate(`/teleconsultation${qs}`);
  };

  // ── Render ──────────────────────────────────────────────────────────
  if (blocked) {
    return (
      <div className="fd-page">
        <div className="fd-card fd-centered">
          <FontAwesomeIcon icon={faUserDoctor} className="fd-hero-icon" />
          <h1>{t('title')}</h1>
          <p>{blocked}</p>
        </div>
      </div>
    );
  }

  if (!consent) {
    return <div className="fd-page fd-loading"><FontAwesomeIcon icon={faSpinner} spin /></div>;
  }

  if (!consent.service_enabled) {
    return (
      <div className="fd-page">
        <div className="fd-card fd-centered">
          <FontAwesomeIcon icon={faUserDoctor} className="fd-hero-icon" />
          <h1>{t('title')}</h1>
          <p>{t('serviceDisabled')}</p>
        </div>
      </div>
    );
  }

  if (!consent.granted) {
    return (
      <div className="fd-page">
        <div className="fd-card fd-consent">
          <div className="fd-consent-head">
            <FontAwesomeIcon icon={faShieldHeart} className="fd-hero-icon" />
            <div>
              <h1>{t('consent.title')}</h1>
              <p>{t('consent.intro')}</p>
            </div>
          </div>
          <div className="fd-warning" role="note">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{t('consent.notConsultation')}</span>
          </div>
          <div className="fd-consent-grid">
            <section>
              <h2><FontAwesomeIcon icon={faCircleCheck} className="fd-ok" /> {t('consent.sharedTitle')}</h2>
              <ul>
                <li>{t('consent.shared1')}</li>
                <li>{t('consent.shared2')}</li>
                <li>{t('consent.shared3')}</li>
                <li>{t('consent.shared4')}</li>
              </ul>
            </section>
            <section>
              <h2><FontAwesomeIcon icon={faCircleXmark} className="fd-no" /> {t('consent.notSharedTitle')}</h2>
              <ul>
                <li>{t('consent.notShared1')}</li>
                <li>{t('consent.notShared2')}</li>
              </ul>
            </section>
          </div>
          <p className="fd-muted">{t('consent.storage')}</p>
          <p className="fd-muted">{t('consent.family')}</p>
          {notice && <div className="fd-notice">{notice}</div>}
          <div className="fd-consent-actions">
            <button type="button" className="fd-btn fd-btn-ghost" onClick={() => navigate(-1)}>{t('consent.back')}</button>
            <button type="button" className="fd-btn fd-btn-primary" onClick={grant}>{t('consent.agree')}</button>
          </div>
        </div>
      </div>
    );
  }

  const busy = sending || transcribing || recording;
  const showSuggestions = !activeId && messages.length <= 1 && !sending;

  return (
    <div className="fd-page">
      <div className="fd-layout">
        <aside className="fd-sidebar fd-card">
          <button type="button" className="fd-btn fd-btn-primary fd-new" onClick={startNew} disabled={sending}>
            <FontAwesomeIcon icon={faPlus} /> {t('newConsultation')}
          </button>
          <h2 className="fd-side-title">{t('recentConsultations')}</h2>
          {conversations.length === 0 && <p className="fd-muted">{t('noConsultations')}</p>}
          <ul className="fd-conv-list">
            {conversations.map((c) => (
              <li key={c.id} className={c.id === activeId ? 'active' : ''}>
                <button type="button" className="fd-conv" onClick={() => openConversation(c.id)} disabled={sending}>
                  <span className="fd-conv-title">{c.title || t('title')}</span>
                  <span className="fd-conv-date">{new Date(c.updated_at || c.created_at).toLocaleString(i18n.language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </button>
                <button type="button" className="fd-icon-btn fd-danger-icon" aria-label={t('deleteConsultation')} title={t('deleteConsultation')} onClick={() => deleteConversation(c.id)}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </li>
            ))}
          </ul>
          <p className="fd-muted fd-small">{t('keepsLast')}</p>
          <button type="button" className="fd-link" onClick={withdraw}>{t('withdraw')}</button>
        </aside>

        <section className="fd-chat fd-card">
          <header className="fd-chat-head">
            <div className="fd-avatar"><FontAwesomeIcon icon={faUserDoctor} /></div>
            <div className="fd-chat-title">
              <h1>{t('title')}</h1>
              <span className="fd-profile-chip">{t('consultingFor', { name: activeProfile && activeProfile.type !== 'self' ? activeProfile.name : t('you') })}</span>
            </div>
            <label className="fd-switch">
              <input type="checkbox" checked={voiceReplies} onChange={toggleVoiceReplies} />
              <span className="fd-switch-track" aria-hidden="true" />
              <span>{t('voiceReplies')}</span>
            </label>
          </header>

          <div className="fd-warning fd-chat-warning" role="note">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{t('disclaimer')}</span>
          </div>

          <div className="fd-messages" aria-live="polite">
            {messages.map((m) => (
              <div key={m.key} className={`fd-msg fd-${m.role}${m.emergency ? ' fd-emergency' : ''}`}>
                {m.emergency && (
                  <div className="fd-emergency-title"><FontAwesomeIcon icon={faTriangleExclamation} /> {t('emergencyTitle')}</div>
                )}
                <div className="fd-bubble">{formatContent(m.content)}</div>
                {m.role === 'assistant' && m.id ? (
                  <button
                    type="button"
                    className="fd-listen"
                    onClick={() => (playingId === m.id ? stopAudio() : play(m.id as number))}
                    disabled={loadingAudioId === m.id}
                  >
                    <FontAwesomeIcon icon={loadingAudioId === m.id ? faSpinner : playingId === m.id ? faStop : faVolumeHigh} spin={loadingAudioId === m.id} />
                    {playingId === m.id ? t('stopAudio') : t('playReply')}
                  </button>
                ) : null}
                {(m.proposals || []).map((p) => (
                  <div key={p.token} className="fd-proposal">
                    <div>
                      <div className="fd-proposal-title">{t('proposalTitle')}</div>
                      <div className="fd-proposal-summary">{p.summary}</div>
                      {p.note && <div className={`fd-proposal-note ${p.status}`}>{p.note}</div>}
                    </div>
                    {p.status !== 'saved' && (
                      <div className="fd-proposal-actions">
                        <button type="button" className="fd-btn fd-btn-ghost" onClick={() => updateProposal(m.key, p.token, null)} disabled={p.status === 'saving'}>{t('dismiss')}</button>
                        <button type="button" className="fd-btn fd-btn-primary" onClick={() => saveProposal(m.key, p)} disabled={p.status === 'saving'}>
                          {p.status === 'saving' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />} {t('save')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {m.consultation?.recommended && !m.emergency && (
                  <div className="fd-consult-card">
                    <div className="fd-proposal-title"><FontAwesomeIcon icon={faUserDoctor} /> {isPrimaryCareOnly(m.consultation.specializations) ? t('consultGeneralPhysician') : t('consultSpecialist')}</div>
                    {m.consultation.specializations.length > 0 && (
                      <p className="fd-analysis-specs">
                        {m.consultation.specializations.map((s) => s.name).join(', ')}
                        {m.consultation.urgency !== 'routine' && (
                          <span className={`fd-urgency fd-urgency-${m.consultation.urgency}`}>
                            {t(`urgency.${m.consultation.urgency}`)}
                          </span>
                        )}
                      </p>
                    )}
                    {(m.consultation.reason || m.consultation.specializations.length === 0) && (
                      <p className="fd-proposal-summary">{m.consultation.reason || t('consultInPerson')}</p>
                    )}
                    {m.consultation.specializations.length > 0 && (
                      <div className="fd-proposal-actions">
                        <button type="button" className="fd-btn fd-btn-primary" onClick={() => bookFromChat(m.consultation as ConsultationAdvice)}>
                          <FontAwesomeIcon icon={faCalendarCheck} /> {t('bookAppointment')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {sending && <div className="fd-msg fd-assistant"><div className="fd-bubble fd-typing">{t('thinking')}</div></div>}
            {showSuggestions && (
              <div className="fd-suggestions">
                {(['q1', 'q2', 'q3', 'q4'] as const).map((q) => (
                  <button type="button" key={q} onClick={() => send(t(`suggestions.${q}`))}>{t(`suggestions.${q}`)}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {(docBusy || analysis) && (
            <div className="fd-analysis">
              {docBusy && <p className="fd-analysis-busy"><FontAwesomeIcon icon={faSpinner} spin /> {t('processingReport')}</p>}
              {!docBusy && analysis && (
                <>
                  <div className="fd-analysis-head">{t('analysisIntro')}</div>
                  {analysis.emergency && (
                    <div className="fd-emergency-banner" role="alert">
                      <div className="fd-emergency-title"><FontAwesomeIcon icon={faTriangleExclamation} /> {t('emergencyTitle')}</div>
                      {analysis.emergency_advice && <p>{analysis.emergency_advice}</p>}
                    </div>
                  )}
                  {analysis.summary && <p className="fd-analysis-summary">{analysis.summary}</p>}

                  {analysis.records.length > 0 && (
                    <div className="fd-analysis-card">
                      <h3><FontAwesomeIcon icon={faCheck} /> {t('reportReadings')}</h3>
                      {analysis.records.map((r, i) => (
                        <label key={`${r.vital}-${i}`} className="fd-doc-row">
                          <input
                            type="checkbox"
                            checked={!!docSelected[i]}
                            disabled={docsSaved}
                            onChange={() => setDocSelected((s) => s.map((v, j) => (j === i ? !v : v)))}
                          />
                          {describeRecord(r)}
                        </label>
                      ))}
                      <div className="fd-proposal-actions">
                        {docsSaved ? (
                          <span className="fd-analysis-done"><FontAwesomeIcon icon={faCircleCheck} className="fd-ok" /> {t('reportSaved')}</span>
                        ) : (
                          <button type="button" className="fd-btn fd-btn-primary" onClick={saveDocRecords} disabled={savingDocs || !docSelected.some(Boolean)}>
                            {savingDocs ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />} {t('saveSelected')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {analysis.recommended_tests.length > 0 && (
                    <div className="fd-analysis-card">
                      <h3><FontAwesomeIcon icon={faClipboardList} /> {t('recommendedTests')}</h3>
                      <ul className="fd-analysis-list">
                        {analysis.recommended_tests.map((test, i) => (
                          <li key={`${test.name}-${i}`}>
                            <strong>{test.name}</strong>{test.reason ? ` — ${test.reason}` : ''}
                          </li>
                        ))}
                      </ul>
                      <p className="fd-analysis-note">{t('testsNote')}</p>
                    </div>
                  )}

                  {analysis.consultation.recommended && !analysis.emergency && (
                    <div className="fd-analysis-card">
                      <h3><FontAwesomeIcon icon={faUserDoctor} /> {isPrimaryCareOnly(analysis.consultation.specializations) ? t('consultGeneralPhysician') : t('consultSpecialist')}</h3>
                      {analysis.consultation.specializations.length > 0 && (
                        <p className="fd-analysis-specs">
                          {analysis.consultation.specializations.map((s) => s.name).join(', ')}
                          {analysis.consultation.urgency !== 'routine' && (
                            <span className={`fd-urgency fd-urgency-${analysis.consultation.urgency}`}>
                              {t(`urgency.${analysis.consultation.urgency}`)}
                            </span>
                          )}
                        </p>
                      )}
                      {(analysis.consultation.reason || analysis.consultation.specializations.length === 0) && (
                        <p>{analysis.consultation.reason || t('consultInPerson')}</p>
                      )}
                      {analysis.consultation.specializations.length > 0 && (
                        <div className="fd-proposal-actions">
                          <button type="button" className="fd-btn fd-btn-primary" onClick={bookFromAnalysis}>
                            <FontAwesomeIcon icon={faCalendarCheck} /> {t('bookAppointment')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="fd-analysis-actions">
                    <button type="button" className="fd-btn fd-btn-ghost" onClick={dismissAnalysis}>{t('dismiss')}</button>
                  </div>
                </>
              )}
            </div>
          )}

          {notice && <div className="fd-notice" role="status">{notice}</div>}
          {(recording || transcribing) && <div className="fd-recording">{recording ? t('listening') : t('transcribing')}</div>}

          <form className="fd-composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <button type="button" className="fd-icon-btn" title={`${t('uploadReport')} (${t('uploadHint')})`} aria-label={t('uploadReport')} onClick={() => fileRef.current?.click()} disabled={docBusy || busy}>
              <FontAwesomeIcon icon={faFileArrowUp} />
            </button>
            <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,application/pdf,image/*" onChange={onFile} />
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              maxLength={2000}
              placeholder={t('placeholder')}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              disabled={sending}
            />
            <button
              type="button"
              className={`fd-icon-btn fd-mic${recording ? ' recording' : ''}`}
              aria-label={recording ? t('stopRecording') : t('startRecording')}
              title={recording ? t('stopRecording') : t('startRecording')}
              onClick={() => (recording ? stopRecorder() : startRecording())}
              disabled={sending || transcribing}
            >
              <FontAwesomeIcon icon={recording ? faStop : transcribing ? faSpinner : faMicrophone} spin={transcribing} />
            </button>
            <button type="submit" className="fd-send" aria-label={t('send')} disabled={!input.trim() || busy}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </form>
        </section>
      </div>

      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        confirmLabel={confirm.confirmLabel}
        variant={confirm.variant}
        onConfirm={confirm.onConfirm}
        onClose={() => setConfirm((d) => ({ ...d, open: false }))}
      />
    </div>
  );
};

export default AIFamilyDoctor;
